import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Switch,
  Chip,
  Divider,
} from "@mui/material";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { errorTost } from "../../Services/Toast";
import { getOverlappingPopScopes } from "../../Services/Brokerage";

const steps = [
  "Plan Details",
  "POP Validity",
  "Scope Type",
  "Scope Selection",

  "Calculation Rules",
  "Eligibility",
  "Conflict Settings",
  "CP Sign-Off",
  "Review",
];

const CreatePopPlanStep4 = ({ data, setData, onBack, onNext }) => {
  useEffect(() => {
    if (data.isActive === undefined) {
      setData((prev) => ({
        ...prev,
        isActive: true,
      }));
    }
  }, []);
  const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"

  return (
    <Box sx={{ p: 1 }}>
      {/* Back */}
      <Typography
        variant="body2"
        sx={{ mb: 2, cursor: "pointer", color: "text.secondary" }}
        onClick={onBack}
      >
        ← Back to POPs
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={1} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  width: 36,
                  height: 36,
                  fontSize: "1rem",
                  "&.Mui-completed": { color: "#2563EB" },
                  "&.Mui-active": {
                    color: "#2563EB",
                    transform: "scale(1.15)",
                  },
                },
              }}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: "0.85rem",
                  mt: 1,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Step 4 of 9: <strong>POP Validity</strong> — Set duration
      </Typography>

      {/* MAIN CONTAINER (Flat) */}
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#FFFFFF",
          maxWidth: 900,
        }}
      >
        {/* Title */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <CalendarMonthOutlinedIcon sx={{ color: "#C4D9F5", mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            POP Validity Period
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Date Fields */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 3,
            maxWidth: 720,
            mb: 3,
          }}
        >
          {/* Start Date */}
          <Box>
            <Typography fontWeight={500} mb={1}>
              Start Date
            </Typography>

            <TextField
              fullWidth
              size="small"
              type="date"
              value={data.startDate || ""}
              onChange={(e) =>
                setData({
                  ...data,
                  startDate: e.target.value,
                })
              }
            />
          </Box>

          {/* End Date */}
          <Box>
            <Typography fontWeight={500} mb={1}>
              End Date
            </Typography>

            <TextField
              fullWidth
              size="small"
              type="date"
              value={data.endDate || ""}
              inputProps={{ min: data.startDate }}
              onChange={(e) =>
                setData({
                  ...data,
                  endDate: e.target.value,
                })
              }
            />
          </Box>
        </Box>

        {/* Status Section */}
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: 720,
            backgroundColor: "#F9FAFB",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeOutlinedIcon sx={{ color: "#9CA3AF" }} />

            <Box>
              <Typography fontWeight={600}>Status</Typography>

              <Typography fontSize={13} color="text.secondary">
                Activate this POP immediately
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {data.isActive && (
              <Chip
                label="Active"
                size="small"
                sx={{
                  bgcolor: "#DCFCE7",
                  color: "#16A34A",
                  fontWeight: 600,
                }}
              />
            )}

            <Switch
              checked={!!data.isActive}
              onChange={(e) =>
                setData({
                  ...data,
                  isActive: e.target.checked,
                })
              }
            />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
        }}
      >
        <Button variant="outlined" onClick={onBack}>
          ← Back
        </Button>

        <Button
          variant="contained"
          onClick={async () => {
            if (new Date(data.endDate) < new Date(data.startDate)) {
              errorTost("End date cannot be before start date");
              return;
            }

            try {
              const overlap = await getOverlappingPopScopes(
                data.projectId,
                data.startDate,
                data.endDate,
              );

              if (overlap.projectBlocked) {
                errorTost(
                  "Another POP plan already exists for this project in the selected date range",
                );
                return;
              }

              onNext();
            } catch (err) {
              console.error(err);
              errorTost("Failed to validate POP dates");
            }
          }}
          disabled={!data.startDate || !data.endDate}
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePopPlanStep4;
